import React, { useState, useEffect } from 'react';
import { Upload, AlertTriangle, CheckCircle, X, Filter, Image as ImageIcon, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = getBackendUrl() + '/api';

export const DeficienciesTab = ({ projectId, userRole }) => {
  const [deficiencies, setDeficiencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchDeficiencies();
  }, [projectId, statusFilter]);

  const fetchDeficiencies = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? `?status_filter=${statusFilter}` : '';
      const response = await axios.get(`${API}/projects/${projectId}/deficiencies${params}`);
      setDeficiencies(response.data);
    } catch (error) {
      console.error('Error fetching deficiencies:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to load deficiencies');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    if (photos.length === 0) {
      toast.error('Please add at least one photo');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('title', title);
      if (description) formData.append('description', description);
      formData.append('priority', priority);
      
      photos.forEach((photo) => {
        formData.append('photos', photo);
      });

      await axios.post(`${API}/projects/${projectId}/deficiencies`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      toast.success('Deficiency reported successfully');
      setTitle('');
      setDescription('');
      setPriority('medium');
      setPhotos([]);
      setUploadProgress(0);
      
      // Small delay before closing to show completion
      setTimeout(() => {
        setIsDialogOpen(false);
      }, 500);
      
      fetchDeficiencies();
    } catch (error) {
      console.error('Error creating deficiency:', error);
      toast.error(error.response?.data?.detail || 'Failed to create deficiency');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleStatusUpdate = async (deficiencyId, newStatus) => {
    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      
      await axios.put(`${API}/projects/${projectId}/deficiencies/${deficiencyId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`Deficiency marked as ${newStatus}`);
      fetchDeficiencies();
    } catch (error) {
      console.error('Error updating deficiency:', error);
      toast.error('Failed to update deficiency');
    }
  };

  const handleDelete = async (deficiencyId, deficiencyTitle) => {
    if (!window.confirm(`Delete "${deficiencyTitle}"? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/projects/${projectId}/deficiencies/${deficiencyId}`);
      toast.success('Deficiency deleted');
      fetchDeficiencies();
    } catch (error) {
      console.error('Error deleting deficiency:', error);
      toast.error('Failed to delete deficiency');
    }
  };

  const canEditDeficiency = (deficiency, currentUserRole) => {
    // Admin can edit any deficiency
    if (currentUserRole === 'admin') return true;
    // Employees can only edit their own
    return deficiency.created_by_id === deficiency.created_by_id; // Will need to pass user ID
  };

  const canDeleteDeficiency = (deficiency, currentUserRole) => {
    // Only admin can delete
    return currentUserRole === 'admin';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-300 bg-red-900/50 border-red-500';
      case 'medium': return 'text-yellow-300 bg-yellow-900/50 border-yellow-500';
      case 'low': return 'text-blue-300 bg-blue-900/50 border-blue-500';
      default: return 'text-gray-300 bg-gray-900/50 border-gray-500';
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'resolved') {
      return (
        <span className="flex items-center gap-1.5 text-sm font-bold text-green-300 bg-green-900/50 border-2 border-green-500 px-3 py-1.5 rounded-full">
          <CheckCircle className="w-4 h-4" />
          Resolved
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1.5 text-sm font-bold text-orange-300 bg-orange-900/50 border-2 border-orange-500 px-3 py-1.5 rounded-full">
        <AlertTriangle className="w-4 h-4" />
        Open
      </span>
    );
  };

  const filteredDeficiencies = deficiencies;

  const openCount = deficiencies.filter(d => d.status === 'open').length;
  const resolvedCount = deficiencies.filter(d => d.status === 'resolved').length;

  return (
    <div className="space-y-4">
      {/* Header with Stats and Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-orange-900/30 border border-orange-600 px-4 py-2 rounded-lg">
            <p className="text-sm text-orange-300">Open</p>
            <p className="text-2xl font-bold text-white">{openCount}</p>
          </div>
          <div className="bg-green-900/30 border border-green-600 px-4 py-2 rounded-lg">
            <p className="text-sm text-green-300">Resolved</p>
            <p className="text-2xl font-bold text-white">{resolvedCount}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          {/* Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>

          {/* Add Deficiency Button */}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-orange-500 to-red-500">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Report Issue
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 text-white border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Report Deficiency</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Cracked drywall in bedroom"
                    required
                    className="mt-1 text-gray-900 placeholder:text-gray-400"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Provide details about the issue..."
                    className="mt-1 text-gray-900 placeholder:text-gray-400"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger className="mt-1 text-gray-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="photos">Photos *</Label>
                  <Input
                    id="photos"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => setPhotos(Array.from(e.target.files))}
                    className="mt-1"
                    required
                    disabled={uploading}
                  />
                  {photos.length > 0 && !uploading && (
                    <p className="text-sm text-slate-400 mt-2">
                      {photos.length} photo{photos.length > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

                {/* Upload Progress */}
                {uploading && (
                  <div className="space-y-3 animate-in fade-in-0 duration-300">
                    <div className="p-4 bg-orange-900/50 border-2 border-orange-600 rounded-lg">
                      <div className="flex items-center gap-3 mb-3">
                        <Upload className="w-6 h-6 text-orange-300 animate-bounce" />
                        <div className="flex-1">
                          <p className="text-base font-bold text-white">
                            Uploading Deficiency Photos
                          </p>
                          <p className="text-sm text-orange-200 mt-1">
                            {photos.length} photo{photos.length > 1 ? 's' : ''} • {uploadProgress}% complete
                          </p>
                        </div>
                        <span className="text-2xl font-bold text-white bg-orange-600 px-3 py-1 rounded-lg">
                          {uploadProgress}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-900 rounded-full h-3 overflow-hidden border border-slate-700">
                        <div 
                          className="bg-gradient-to-r from-orange-500 via-orange-400 to-orange-500 h-3 rounded-full transition-all duration-300 ease-out animate-pulse"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                    <p className="text-center text-sm text-slate-400 animate-pulse">
                      ⏳ Please wait... Do not close this window
                    </p>
                  </div>
                )}

                {!uploading && (
                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      disabled={uploading}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-red-500"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Submit
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                      disabled={uploading}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-blue-900/60 border-2 border-blue-500 rounded-lg shadow-lg">
        <p className="text-base font-semibold text-white">
          🔒 <strong className="text-blue-200">Internal Only:</strong> Deficiencies are visible to admin and employees only. <span className="text-blue-100">Clients cannot see this section.</span>
        </p>
      </div>

      {/* Deficiencies List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          <p className="text-slate-400 mt-4">Loading deficiencies...</p>
        </div>
      ) : filteredDeficiencies.length === 0 ? (
        <div className="text-center py-12 glass-card">
          <AlertTriangle className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">
            {statusFilter === 'all' ? 'No deficiencies reported yet' : `No ${statusFilter} deficiencies`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredDeficiencies.map((deficiency) => (
            <div
              key={deficiency.id}
              className="bg-slate-800/90 rounded-lg p-5 border-l-4 shadow-lg"
              style={{
                borderLeftColor: 
                  deficiency.priority === 'high' ? '#ef4444' : 
                  deficiency.priority === 'medium' ? '#f59e0b' : '#3b82f6'
              }}
            >
              {/* Header */}
              <div className="mb-4">
                <h3 className="text-white font-bold text-xl mb-3">{deficiency.title}</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  {getStatusBadge(deficiency.status)}
                  <span className={`text-sm px-3 py-1.5 rounded-full border-2 font-bold ${getPriorityColor(deficiency.priority)}`}>
                    {deficiency.priority.toUpperCase()} PRIORITY
                  </span>
                </div>
              </div>

              {/* Description */}
              {deficiency.description && (
                <p className="text-white text-base mb-4 leading-relaxed">{deficiency.description}</p>
              )}

              {/* Photos */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {deficiency.photos.map((photo, idx) => {
                  // Handle both full URLs and relative paths
                  const photoUrl = photo.url.startsWith('http') 
                    ? photo.url 
                    : `${getBackendUrl()}${photo.url}`;
                  
                  return (
                    <a
                      key={idx}
                      href={photoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aspect-square bg-slate-700 rounded overflow-hidden hover:opacity-80 transition-opacity"
                    >
                      <img
                        src={photoUrl}
                        alt={`Deficiency ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.error('Failed to load image:', photoUrl);
                          e.target.style.display = 'none';
                        }}
                      />
                    </a>
                  );
                })}
              </div>

              {/* Meta Info */}
              <div className="text-sm text-slate-200 mb-4 space-y-1 bg-slate-900/50 p-3 rounded-lg">
                <p className="font-medium">📝 Reported by <span className="text-white">{deficiency.created_by}</span></p>
                <p className="text-slate-300">📅 {new Date(deficiency.created_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}</p>
                {deficiency.resolved_at && (
                  <>
                    <p className="font-medium mt-2">✅ Resolved by <span className="text-white">{deficiency.resolved_by}</span></p>
                    <p className="text-slate-300">📅 {new Date(deficiency.resolved_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}</p>
                  </>
                )}
              </div>

              {/* Actions - Mobile Optimized */}
              <div className="space-y-2">
                {/* Primary Action - Status Toggle */}
                {deficiency.status === 'open' ? (
                  <Button
                    onClick={() => handleStatusUpdate(deficiency.id, 'resolved')}
                    className="w-full bg-green-600 hover:bg-green-700 h-11 text-base font-semibold"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Mark as Resolved
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => handleStatusUpdate(deficiency.id, 'open')}
                    className="w-full h-11 text-base font-semibold border-orange-600 text-orange-400 hover:bg-orange-900/30"
                  >
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Reopen Issue
                  </Button>
                )}

                {/* Admin Only - Delete Button */}
                {userRole === 'admin' && (
                  <Button
                    onClick={() => handleDelete(deficiency.id, deficiency.title)}
                    variant="outline"
                    className="w-full border-red-600 text-red-400 hover:bg-red-900/30 h-10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
